import * as z from "zod";
import { creditNoteDocumentDto } from "../documents/credit-note-document";
import { decimal, flag, pagination, version } from "../index";

// Shipments the warehouse closed short (50-api/sales.md; ADR-178 §1.10, ADR-199): the units of a shipment invoiced
// before it left that did not leave, with their value, for Finance to credit. From F2 (B-54 point 3, ADR-230) Finance
// approves the credit note here and documents asks Business Central for it (I-04 `credit_note`); recording the number
// of one made there by hand stays, for what does not pass through Nexxt.

const shortShipmentFilters = {
  /**
   * Finance approved its credit note or recorded one (true), or not yet (false): the queue of Finance is
   * `credited: false`.
   */
  credited: flag.optional(),
  customer: z.uuid().optional(),
  /** Part of the number of the sales order, by its letters and digits (ADR-117). */
  q: z.string().trim().min(1).max(30).optional(),
};

/** The shipments closed short, the oldest first: those to credit wait from when the warehouse closed them. */
export const listShortShipmentsInput = z.object({
  ...shortShipmentFilters,
  ...pagination({ defaultSize: 50, maxSize: 100 }),
});
export type ListShortShipmentsInput = z.input<typeof listShortShipmentsInput>;

/** How many the list has with the same filters, and since when the oldest (ADR-086): the queue of the home. */
export const countShortShipmentsInput = z.object(shortShipmentFilters);
export type CountShortShipmentsInput = z.input<typeof countShortShipmentsInput>;

export const shortShipmentParams = z.object({ shortShipmentId: z.uuid() });
export type ShortShipmentParams = z.input<typeof shortShipmentParams>;

/**
 * Finance made the credit note on Business Central: its number, as Business Central gave it, and a note. `version` is
 * the one the client read: one changed in the meantime answers 409.
 */
export const recordShortShipmentCreditInput = shortShipmentParams.extend({
  version,
  creditNoteNo: z.string().trim().min(1).max(40),
  note: z.string().trim().min(1).max(500).optional(),
});
export type RecordShortShipmentCreditInput = z.input<typeof recordShortShipmentCreditInput>;
export const recordShortShipmentCreditBody = recordShortShipmentCreditInput.omit({ shortShipmentId: true });

/**
 * F2 (ADR-230): Finance approves the credit note, which documents asks Business Central for. `version` is the one the
 * client read: one changed in the meantime answers 409.
 */
export const approveShortShipmentCreditInput = shortShipmentParams.extend({ version });
export type ApproveShortShipmentCreditInput = z.input<typeof approveShortShipmentCreditInput>;
export const approveShortShipmentCreditBody = approveShortShipmentCreditInput.omit({ shortShipmentId: true });

/** A shipment closed short, in the list and after the credit note. */
export const shortShipmentDto = z
  .object({
    id: z.uuid(),
    soId: z.uuid(),
    soNumber: z.string(),
    customerOrgId: z.uuid(),
    customer: z.string(),
    fulfillmentId: z.uuid(),
    fulfillmentNumber: z.string().nullable(),
    /** The lot of the order the shipment is of (ADR-195); null for an order locked before the lots. */
    lotNo: z.number().int().nullable(),
    /** The invoice of the shipment, as Business Central numbered it: the one the credit note is against. */
    invoiceNo: z.string().nullable(),
    currency: z.string(),
    /** The units invoiced that did not leave. */
    units: z.number().int(),
    /**
     * Their value, in the currency of the order, after the discounts and without taxes, frozen at the closing; a field
     * rule may hide it, as the amount of an order.
     */
    amount: decimal.optional(),
    /** When sales heard that the warehouse closed the shipment short. */
    closedAt: z.iso.datetime(),
    /** Null until Finance records the credit note. */
    creditNoteNo: z.string().nullable(),
    note: z.string().nullable(),
    creditedBy: z.string().nullable(),
    creditedAt: z.iso.datetime().nullable(),
    /** F2 (ADR-230): who in Finance approved the credit note Nexxt asks of Business Central, and when. */
    creditApprovedBy: z.string().nullable().optional(),
    creditApprovedAt: z.iso.datetime().nullable().optional(),
    /** The credit note documents asked Business Central for, once approved: its state and number. */
    creditNote: creditNoteDocumentDto.nullable().optional(),
    version: z.number().int(),
  })
  .meta({ id: "ShortShipment" });
export type ShortShipmentDto = z.output<typeof shortShipmentDto>;

/** A line of the order with units not shipped: what the credit note says of it. */
export const shortShipmentLineDto = z.object({
  soLineId: z.uuid(),
  lineNo: z.number().int(),
  /** Null for an SKU not yet given its code. */
  skuCode: z.string().nullable(),
  units: z.number().int(),
  /** The price of one unit after the discounts of the line and of the order. */
  unitNet: decimal,
  amount: decimal,
});
export type ShortShipmentLineDto = z.output<typeof shortShipmentLineDto>;

/** A shipment closed short with its lines, in the order of the lines of the order. */
export const shortShipmentDetailDto = shortShipmentDto
  .extend({ lines: z.array(shortShipmentLineDto) })
  .meta({ id: "ShortShipmentDetail" });
export type ShortShipmentDetailDto = z.output<typeof shortShipmentDetailDto>;
